import { HugeiconsIcon } from "@hugeicons/react";
import {
	AdvertisimentIcon as AdvertisimentIconDuotone,
	AiAudioIcon as AiAudioIconDuotone,
	MapsGlobal02Icon as MapsGlobal02IconDuotone,
	MusicNoteSquare01Icon as MusicNoteSquare01IconDuotone,
	NoteEditIcon as NoteEditIconDuotone,
	RssIcon as RssIconDuotone,
	Settings05Icon as Settings05IconDuotone,
	UserMultiple02Icon as UserMultiple02IconDuotone,
} from "@hugeicons-pro/core-duotone-rounded";
import {
	AdvertisimentIcon,
	AiAudioIcon,
	MapsGlobal02Icon,
	MusicNoteSquare01Icon,
	NoteEditIcon,
	RssIcon,
	Settings05Icon,
	UserMultiple02Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Trans, useLingui } from "@lingui/react/macro";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { AppContainer } from "~/components/layouts/app/app-container";
import { NavigationTabLink } from "~/components/navigation-tab-link";
import { PageHead } from "~/components/ui/page-head";
import { ScrollArea, ScrollBar } from "~/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import type { Channel } from "~/models/channel";

export const Route = createFileRoute("/_app/podcasts/$channelUid/settings")({
	component: Page,
});

function Page() {
	const { channelUid } = Route.useParams();

	const { t } = useLingui();

	return (
		<>
			<PageHead
				icon={Settings05IconDuotone}
				title={t`Podcast Settings`}
				subtitle={t`Manage your podcast, the jingles & the settings.`}
				navigation={<ChannelNavigation channelUid={channelUid} />}
			/>
			<AppContainer className="my-12">
				<Outlet />
			</AppContainer>
		</>
	);
}

function ChannelNavigation({ channelUid }: { channelUid: Channel["uid"] }) {
	return (
		<Tabs>
			<ScrollArea className="-mb-[2px]">
				<TabsList className="text-foreground h-auto gap-2 rounded-none bg-transparent px-0">
					<TabsTrigger value="statistics" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/settings/details"
							params={{ channelUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={NoteEditIcon}
										altIcon={NoteEditIconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 size-4"
									/>
									<Trans>Details</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
					<TabsTrigger value="monetization" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/settings/monetization"
							params={{ channelUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={AdvertisimentIcon}
										altIcon={AdvertisimentIconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 size-4"
									/>
									<Trans>Monetization</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
					<TabsTrigger value="audio" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/settings/audio"
							params={{ channelUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={AiAudioIcon}
										altIcon={AiAudioIconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 size-4"
									/>
									<Trans>Audio</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
					<TabsTrigger value="jingles" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/settings/jingles"
							params={{ channelUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={MusicNoteSquare01Icon}
										altIcon={MusicNoteSquare01IconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 size-4"
									/>
									<Trans>Jingles</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
					<TabsTrigger value="geolocation" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/settings/geolocation"
							params={{ channelUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={MapsGlobal02Icon}
										altIcon={MapsGlobal02IconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 size-4"
									/>
									<Trans>Geolocation</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
					<TabsTrigger value="team" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/settings/team"
							params={{ channelUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={UserMultiple02Icon}
										altIcon={UserMultiple02IconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 size-4"
									/>
									<Trans>Team</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
					<TabsTrigger value="directories" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/settings/directories"
							params={{ channelUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={RssIcon}
										altIcon={RssIconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 size-4"
									/>
									<Trans>Directories</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
					<TabsTrigger value="settings" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/settings/settings"
							params={{ channelUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={Settings05Icon}
										altIcon={Settings05IconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 size-4"
									/>
									<Trans>Settings</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
				</TabsList>
				<ScrollBar orientation="horizontal" />
			</ScrollArea>
		</Tabs>
	);
}
